import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export interface TourSocialLink {
  icon: string;
  label: string;
  url: string;
}

/** Daily generation caps for the social auto-posting agent. */
export interface SocialLimits {
  threadsPerDay: number;
  instagramPerDay: number;
  storiesPerDay: number;
  /** Carousel auto-post cadence in days (0 = off). */
  carouselEveryDays: number;
  /** Reel auto-post cadence in days (0 = off). */
  reelEveryDays: number;
  /** TikTok auto-post cadence in days (0 = off). */
  tiktokEveryDays: number;
}

/** Price per event-boost tier in EUR, keyed by duration in hours. */
export interface BoostPrices {
  '4': number;
  '12': number;
  '24': number;
  '72': number;
  '168': number;
}

export interface AppSettings {
  id: string;
  isMaintenanceMode: boolean;
  isIngestionEnabled: boolean;
  warningPopupText: string;
  requiredMinVersionIos: string;
  suggestedMinVersionIos: string;
  requiredMinVersionAndroid: string;
  suggestedMinVersionAndroid: string;
  tourSocialLinks: TourSocialLink[] | null;
  socialLimits: SocialLimits | null;
  isSocialEnabled: boolean;
  isSocialAutoApprove: boolean;
  isAutoPublishEnabled: boolean;
  autoPublishPerCity: number;
  // Daily localized "come back / new events in your city" pushes to inactive
  // users (18:00, per environment). May be absent on older backends.
  isReengagementPushEnabled?: boolean;
  // Payments: platform application fee (percent + fixed, major units) and the
  // minimum paid ticket/membership price. May be absent on older backends.
  platformFeePercent?: number;
  platformFeeFixed?: number;
  minPaidPrice?: number;
  // Event-boost tier prices in EUR (null = backend defaults).
  boostPrices?: BoostPrices | null;
  // Master kill switch for taking money: false = the backend refuses to create
  // any new charge. Absent on older backends — treat that as enabled.
  isPaymentsEnabled?: boolean;
  // Nightly purge of abandoned past events (03:30 UTC) and how long an event is
  // kept before it is eligible. Absent on older backends — treat as off / 30d.
  isEventPurgeEnabled?: boolean;
  eventPurgeRetentionDays?: number;
  // Anti-fraud controls, sized from the August 2026 card-testing attack.
  // May be absent on older backends — callers must default them.
  velocityWindowMinutes?: number;
  maxFailedPayments?: number;
  maxSucceededPayments?: number;
  isForce3dsEnabled?: boolean;
  payoutMinAccountAgeHours?: number;
  // Interest-based event suggestion pushes (17:00, one hour before the
  // re-engagement cron). The five tunables read back as null on rows written
  // before the columns existed — null means "backend default", never "no limit".
  isEventSuggestionPushEnabled?: boolean;
  suggestionMaxPerDay?: number | null;
  suggestionMaxPerWeek?: number | null;
  suggestionMinScoreExact?: number | null;
  suggestionMinScoreBridge?: number | null;
  suggestionMaxUsersPerEvent?: number | null;
}

export interface UpdateAppSettingsArgs {
  isMaintenanceMode: boolean;
  isIngestionEnabled?: boolean;
  warningPopupText: string;
  requiredMinVersionIos?: string;
  suggestedMinVersionIos?: string;
  requiredMinVersionAndroid?: string;
  suggestedMinVersionAndroid?: string;
  tourSocialLinks?: TourSocialLink[] | null;
  socialLimits?: SocialLimits | null;
  isSocialEnabled?: boolean;
  isSocialAutoApprove?: boolean;
  isAutoPublishEnabled?: boolean;
  autoPublishPerCity?: number;
  isReengagementPushEnabled?: boolean;
  platformFeePercent?: number;
  platformFeeFixed?: number;
  minPaidPrice?: number;
  boostPrices?: BoostPrices;
  isPaymentsEnabled?: boolean;
  isEventPurgeEnabled?: boolean;
  eventPurgeRetentionDays?: number;
  // Anti-fraud. The API reads the 3DS flag back as `isForce3dsEnabled` but takes
  // it as `force3ds` on write — the entity column and the settings interface are
  // named differently on the backend.
  velocityWindowMinutes?: number;
  maxFailedPayments?: number;
  maxSucceededPayments?: number;
  force3ds?: boolean;
  payoutMinAccountAgeHours?: number;
  isEventSuggestionPushEnabled?: boolean;
  suggestionMaxPerDay?: number;
  suggestionMaxPerWeek?: number;
  suggestionMinScoreExact?: number;
  suggestionMinScoreBridge?: number;
  suggestionMaxUsersPerEvent?: number;
}

export type SocialPostPlatform =
  | 'threads'
  | 'instagram'
  | 'instagram_story'
  | 'instagram_carousel'
  | 'instagram_reel'
  | 'tiktok';

export interface SocialPostResult {
  id: string;
  platform: SocialPostPlatform;
  status: 'draft' | 'approved' | 'publishing' | 'published' | 'rejected' | 'failed';
  imageUrl: string | null;
}

/** One re-engagement push pass (POST /admin/notifications/reengagement/run). */
export interface ReengagementRunResult {
  enabled: boolean;
  dryRun: boolean;
  eligibleUsers: number;
  sent: number;
  failed: number;
  sample: {
    userId: string;
    language: string | null;
    city: string | null;
    newEventsCount: number;
    key: string;
    title: string;
    body: string;
  }[];
}

/** Which daily pass produced a suggestion. Mirrors the backend `SuggestionKind`. */
export type SuggestionKind = 'new_event' | 'starting_soon';

/** How the pair matched: shared activity ids (`exact`) or bridged labels (`bridge`). */
export type SuggestionMatchConfidence = 'exact' | 'bridge';

/** One suggestion pass (POST /admin/notifications/suggestions/run). */
export interface SuggestionRunResult {
  enabled: boolean;
  dryRun: boolean;
  kind: SuggestionKind;
  eligibleUsers: number;
  skippedNoCity: number;
  skippedDailyCap: number;
  skippedWeeklyCap: number;
  skippedOverBroad: number;
  skippedNoMatch: number;
  skippedFanoutCap: number;
  candidatesByCity: Record<string, number>;
  // The number that decides whether the category bridge is pulling its weight
  // or just adding noise.
  matchedExact: number;
  matchedBridge: number;
  sent: number;
  failed: number;
  sample: {
    userId: string;
    language: string | null;
    city: string | null;
    eventId: string;
    eventName: string;
    kind: SuggestionKind;
    matchConfidence: SuggestionMatchConfidence;
    score: number;
    key: string;
    title: string;
    body: string;
  }[];
}

/** GET /admin/notifications/suggestions/coverage — read-only, composes nothing. */
export interface SuggestionCoverageResult {
  timezone: string;
  eligibleUsers: number;
  usersWithInterests: number;
  usersWithoutCity: number;
  cities: {
    city: string;
    newEventPool: number;
    startingSoonPool: number;
    withActivities: number;
    categoryOnly: number;
  }[];
}

/**
 * One run of the abandoned-event purge
 * (POST /admin/event-cleanup/purge/execute). With `dryRun` the counts are what
 * a real run would delete and nothing is touched.
 */
export interface EventPurgeResult {
  eventsDeleted: number;
  notificationsDeleted: number;
  batches: number;
  dryRun: boolean;
  durationMs: number;
  sample: { id: string; name: string; source: string; endDate: string }[];
  errors: string[];
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery,
  tagTypes: ['Settings'],
  endpoints: (builder) => ({
    getSettings: builder.query<AppSettings, void>({
      query: () => ({ url: '/admin/settings' }),
      providesTags: ['Settings'],
    }),
    updateSettings: builder.mutation<AppSettings, UpdateAppSettingsArgs>({
      query: (body) => ({
        url: '/admin/settings',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Settings'],
    }),
    postSocialNow: builder.mutation<SocialPostResult, SocialPostPlatform>({
      query: (platform) => ({
        url: `/social/generate/${platform}`,
        method: 'POST',
      }),
    }),
    runEventPurge: builder.mutation<EventPurgeResult, { dryRun: boolean }>({
      query: ({ dryRun }) => ({
        url: '/admin/event-cleanup/purge/execute',
        method: 'POST',
        // The ceiling matters: 200 x 10 is enough for the biggest backlog we
        // have seen (1,796 on prod) and still bounded if the predicate ever
        // starts matching more than it should.
        body: { dryRun, batchSize: 200, maxBatches: 10 },
      }),
    }),
    runReengagementPush: builder.mutation<ReengagementRunResult, { dryRun: boolean }>({
      query: ({ dryRun }) => ({
        url: `/admin/notifications/reengagement/run?dryRun=${dryRun}`,
        method: 'POST',
      }),
    }),
    runEventSuggestionPush: builder.mutation<
      SuggestionRunResult,
      { kind: SuggestionKind; dryRun: boolean }
    >({
      query: ({ kind, dryRun }) => ({
        url: `/admin/notifications/suggestions/run?kind=${kind}&dryRun=${dryRun}`,
        method: 'POST',
      }),
    }),
    getSuggestionCoverage: builder.query<SuggestionCoverageResult, void>({
      query: () => ({ url: '/admin/notifications/suggestions/coverage' }),
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  usePostSocialNowMutation,
  useRunReengagementPushMutation,
  useRunEventPurgeMutation,
  useRunEventSuggestionPushMutation,
  // Lazy: the coverage query is only worth a round trip when an admin asks for
  // it, and it is not part of the settings form's load.
  useLazyGetSuggestionCoverageQuery,
} = settingsApi;
